import React from "react";
import { Bike, Sailboat } from "lucide-react";

// A session's rower and bike figures (from telemetry_summary.devices), shown
// under Heart rate on the session's Summary tab: the totals, plus how
// efficiently you worked (utils/telemetry/efficiency).

const clock = (sec) => `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, "0")}`;

const tilesFor = (type, d) => {
  const tiles = [];
  if (d.total_distance_m != null) {
    tiles.push(
      type === "rower"
        ? { label: "metres", value: Math.round(d.total_distance_m).toLocaleString("en-AU") }
        : { label: "km", value: (d.total_distance_m / 1000).toFixed(1) },
    );
  }
  if (type === "rower" && d.pace_sec_per_500m?.avg) tiles.push({ label: "avg /500 m", value: clock(d.pace_sec_per_500m.avg) });
  if (d.power?.avg) tiles.push({ label: "avg watts", value: Math.round(d.power.avg) });
  if (d.energy_kcal > 0) tiles.push({ label: "kcal", value: `${d.energy_source === "power" ? "~" : ""}${Math.round(d.energy_kcal)}` });
  if (type === "indoor_bike" && d.cadence_rpm?.avg) tiles.push({ label: "avg rpm", value: Math.round(d.cadence_rpm.avg) });
  if (type === "rower" && d.total_strokes > 0) tiles.push({ label: "strokes", value: Math.round(d.total_strokes).toLocaleString("en-AU") });
  if (d.resistance?.avg != null) tiles.push({ label: "avg resistance", value: Math.round(d.resistance.avg) });
  if (d.m_per_stroke != null) tiles.push({ label: "m per stroke", value: d.m_per_stroke.toFixed(1) });
  if (d.w_per_beat != null) tiles.push({ label: "watts per beat", value: d.w_per_beat.toFixed(2) });
  if (d.drift_pct != null) {
    tiles.push({
      label: "drift",
      value: `${d.drift_pct > 0 ? d.drift_pct.toFixed(1) : "0"}%`,
      tone: d.drift_pct < 5 ? "text-green-400" : "text-amber-400",
    });
  }
  return tiles;
};

/** "Concept2 PM5", else the machine's Bluetooth name (api/lib/machines.js). */
export const machineLabel = (m) => {
  if (!m) return null;
  const made = [m.manufacturer, m.model].filter(Boolean).join(" ");
  return made || m.name || null;
};

const MachineSummary = ({ devices, machines }) => {
  const shown = ["rower", "indoor_bike"].filter((type) => devices?.[type]);
  if (shown.length === 0) return null;
  const anyEfficiency = shown.some((t) => devices[t].w_per_beat != null || devices[t].drift_pct != null);
  return (
    <div className="grid gap-3">
      {shown.map((type) => {
        const tiles = tilesFor(type, devices[type]);
        if (tiles.length === 0) return null;
        const Icon = type === "rower" ? Sailboat : Bike;
        return (
          <div key={type}>
            <h4 className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-semibold text-muted-foreground">
              <Icon className="h-3 w-3 text-yellow-400" />
              {type === "rower" ? "Rower" : "Bike"}
              {machineLabel(machines?.[type]) && (
                <span className="truncate font-normal">· {machineLabel(machines[type])}</span>
              )}
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {tiles.map((tile) => (
                <div key={tile.label} className="flex flex-col items-center rounded-lg bg-white/5 p-2">
                  <span className={`font-mono text-xl font-bold tabular-nums ${tile.tone || "text-foreground"}`}>
                    {tile.value}
                  </span>
                  <span className="mt-0.5 text-center text-[10px] uppercase tracking-widest text-white/40">{tile.label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {anyEfficiency && (
        <p className="px-1 text-xs leading-snug text-muted-foreground">
          Watts per beat rises as you get fitter. Drift is how much it fell from the first half of a steady piece to the
          second: under 5% is a good aerobic sign.
        </p>
      )}
    </div>
  );
};

export default MachineSummary;
